import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where, limit } from "firebase/firestore";
import { db } from "../firebase";
import { C } from "../ui";

// Shown to a Facility Admin until their building has real activity in it —
// no signup-triggered welcome email or setup wizard exists yet (this *is*
// the onboarding), so this banner is the only thing telling a brand-new
// admin what to do first. Computed live from actual Firestore state
// (never a one-time "have they seen this" flag) so it's inherently
// correct even if steps get done out of order, from a different device,
// or by someone else on the team — and it disappears on its own the
// moment real setup is done, no dismiss button needed.
export default function OnboardingChecklist({ facilityId, vendorCount, onNavigate }) {
  const [workerCount, setWorkerCount] = useState(null);
  const [assetCount, setAssetCount] = useState(null);
  const [hasCompletion, setHasCompletion] = useState(null);
  const [hasTimeCard, setHasTimeCard] = useState(null);

  useEffect(() => onSnapshot(query(collection(db, "workers"), where("facilityId", "==", facilityId)), s =>
    setWorkerCount(s.size)), [facilityId]);
  useEffect(() => onSnapshot(query(collection(db, "assets"), where("facilityId", "==", facilityId)), s =>
    setAssetCount(s.size)), [facilityId]);
  // Either collection having even one doc means the full field-worker loop
  // (QR scan → PIN → checklist/clock-in) has actually run once for real —
  // a much stronger signal than "did they click a test link." Two
  // independent listeners (not nested) so each cleans up on its own.
  useEffect(() => onSnapshot(query(collection(db, "checklistCompletions"), where("facilityId", "==", facilityId), limit(1)), s =>
    setHasCompletion(s.size > 0)), [facilityId]);
  useEffect(() => onSnapshot(query(collection(db, "timeCardEntries"), where("facilityId", "==", facilityId), limit(1)), s =>
    setHasTimeCard(s.size > 0)), [facilityId]);

  if (workerCount === null || assetCount === null || hasCompletion === null || hasTimeCard === null) return null;
  const hasActivity = hasCompletion || hasTimeCard;

  const steps = [
    {
      id: "team", tab: "vendors", done: vendorCount > 0 || workerCount > 0,
      label: "Add your team", sub: "Add a vendor (they'll get an invite code) or an internal staff member.",
    },
    {
      id: "asset", tab: "assets", done: assetCount > 0,
      label: "Add a room or piece of equipment", sub: "This is what a worker scans a QR code to document.",
    },
    {
      id: "activity", tab: "assets", done: hasActivity,
      label: "Complete your first check-in", sub: "Open an asset's “Test link” and run through the real field flow.",
    },
  ];

  if (steps.every(s => s.done)) return null;

  return (
    <div className="app-onboarding" style={{ background: C.tintAccent, border: `1.5px solid ${C.accent}`, borderRadius: 0, padding: "18px 20px", margin: "20px 24px 0" }}>
      <div style={{ fontWeight: 700, marginBottom: 2 }}>Get set up</div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 4 }}>A few steps to get your first real check-in documented.</div>
      {steps.map((step, idx) => (
        <div key={step.id} onClick={() => onNavigate(step.tab)}
          style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 4px", borderTop: idx === 0 ? "none" : `1px solid ${C.border}`, cursor: "pointer" }}>
          <div style={{
            width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
            border: `2px solid ${step.done ? C.green : C.border}`, background: step.done ? C.green : "transparent",
            display: "flex", alignItems: "center", justifyContent: "center", color: C.white, fontSize: 13, fontWeight: 700,
          }}>
            {step.done ? "✓" : ""}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: step.done ? C.muted : C.navy, textDecoration: step.done ? "line-through" : "none" }}>{step.label}</div>
            <div style={{ fontSize: 12, color: C.muted }}>{step.sub}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
